import Reveal from "@/components/Reveal";
import CountUp from "@/components/CountUp";

const CHANNELS = [
  {
    tag: "Meta Ads",
    scope: "Facebook · Instagram · Audience Network",
    users: 3.2,
    usersSuffix: "B+",
    unit: "monthly active users",
    best: "B2C & B2B awareness, retargeting, lead gen",
    formats: ["Image & video ads", "Lead gen forms", "Reels & Stories", "Dynamic retargeting"],
  },
  {
    tag: "Google Display",
    scope: "Search Partners · YouTube · Display Network",
    users: 90,
    usersSuffix: "%+",
    unit: "of internet users reached",
    best: "Intent-driven acquisition & retargeting",
    formats: ["Responsive display", "YouTube pre-roll", "Search ads", "Performance Max"],
  },
  {
    tag: "LinkedIn Ads",
    scope: "Sponsored Content · InMail · Conversation",
    users: 1,
    usersSuffix: "B+",
    unit: "professional members",
    best: "ABM · executive targeting · demand gen",
    formats: ["Sponsored InMail", "Document ads", "Conversation ads", "Lead Gen Forms"],
  },
];

// Pre-computed positions so we don't get hydration flicker
const DOTS = Array.from({ length: 46 }, (_, i) => {
  const seed = (i * 9301 + 49297) % 233280;
  const r = seed / 233280;
  return {
    x: (i * 137.5 + r * 20) % 100,
    y: ((i * 43) % 88) + 6,
    size: 2 + (i % 3),
    dur: 8 + (i % 6) * 2,
    delay: (r * 6).toFixed(2),
    hue: i % 5 === 0 ? "teal" : "white",
  };
});

export default function PaidMedia() {
  return (
    <section
      id="paid-media"
      data-testid="paid-media-section"
      className="relative py-24 md:py-32 border-t border-white/5 bg-[#040810] overflow-hidden"
    >
      {/* Ambient audience-data dot layer */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        {DOTS.map((d, i) => (
          <span
            key={i}
            className="absolute rounded-full audience-dot"
            style={{
              left: `${d.x}%`,
              top: `${d.y}%`,
              width: `${d.size}px`,
              height: `${d.size}px`,
              backgroundColor: d.hue === "teal" ? "rgba(0,229,194,0.6)" : "rgba(255,255,255,0.18)",
              animationDelay: `${d.delay}s`,
              animationDuration: `${d.dur}s`,
            }}
          />
        ))}
        <div className="absolute inset-0 grid-bg opacity-25 [mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_75%)]" />
      </div>

      <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
        <Reveal className="max-w-3xl">
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-teal mb-4">
            Paid Media
          </div>
          <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-[56px] leading-[1] tracking-[-0.02em]">
            Precision targeting across the world{"\u2019"}s most powerful paid channels.
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-4">
          {CHANNELS.map((c, i) => (
            <Reveal key={c.tag} delay={i * 120}>
              <div
                data-testid={`paid-channel-${c.tag.toLowerCase().replace(/\s+/g, "-")}`}
                className="relative rounded-2xl border border-white/[0.06] bg-[#0A121A] p-7 card-hover overflow-hidden h-full"
              >
                {/* subtle animated corner glow */}
                <span
                  className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-teal/[0.08] blur-2xl channel-glow"
                  style={{ animationDelay: `${i * 0.6}s` }}
                />

                <div className="relative">
                  <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-teal mb-6 flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-teal pulse-dot" />
                    {c.tag}
                  </div>
                  <div className="font-display text-4xl md:text-5xl font-semibold mb-1 tracking-tight">
                    <CountUp
                      end={c.users}
                      decimals={c.users % 1 !== 0 ? 1 : 0}
                      suffix={c.usersSuffix}
                    />
                  </div>
                  <div className="text-xs text-muted-foreground mb-6">{c.unit}</div>
                  <div className="text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-2">
                    Scope
                  </div>
                  <div className="text-sm mb-5">{c.scope}</div>
                  <div className="text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-2">
                    Best for
                  </div>
                  <div className="text-sm mb-5">{c.best}</div>
                  <div className="text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-2">
                    Formats
                  </div>
                  <ul className="space-y-1.5">
                    {c.formats.map((f, fi) => (
                      <li
                        key={f}
                        className="text-sm flex items-center gap-2 format-item"
                        style={{ animationDelay: `${i * 0.15 + fi * 0.08 + 0.4}s` }}
                      >
                        <span className="h-1 w-1 rounded-full bg-teal" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes floatY {
          0%, 100% { transform: translateY(0); opacity: 0.2; }
          50% { transform: translateY(-14px); opacity: 0.9; }
        }
        .audience-dot { animation: floatY 12s ease-in-out infinite; filter: blur(0.5px); }

        @keyframes gently {
          0%, 100% { transform: scale(1); opacity: 0.35; }
          50% { transform: scale(1.15); opacity: 0.7; }
        }
        .channel-glow { animation: gently 6s ease-in-out infinite; }

        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateX(-6px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        .format-item { opacity: 0; animation: fadeSlideIn 550ms cubic-bezier(0.22,1,0.36,1) forwards; }
      `}</style>
    </section>
  );
}
